import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { filter, map, Observable, switchMap } from 'rxjs';
import { CanComponentDeactivate } from 'src/app/shared/guards/can-deactivate.guard';
import { ApiErrorResponse } from 'src/app/shared/interfaces/api-error-response';
import { SubSink } from 'subsink';

import {
  MerchantBasicDataViewModel,
  MerchantSiteMerchantTypeViewModel,
  MerchantSiteViewModel,
  PostMerchantSiteRequest,
} from '@app/api/models';
import { DialogService } from '@shared/services/dialog.service';

import { MerchantSiteEditComponent } from '../../components/merchant-site-edit/merchant-site-edit.component';
import { MerchantActions, MerchantSelectors } from '../../store/merchant.slice';

@Component({
  selector: 'app-merchant-site-edit-container',
  templateUrl: './merchant-site-edit-container.component.html',
  styleUrls: ['./merchant-site-edit-container.component.scss'],
})
export class MerchantSiteEditContainerComponent
  implements OnInit, OnDestroy, CanComponentDeactivate
{
  @ViewChild(MerchantSiteEditComponent) form!: MerchantSiteEditComponent;

  isLoading$: Observable<boolean> = this.store.select(
    MerchantSelectors.selectSiteLoading
  );
  error$: Observable<ApiErrorResponse | undefined> = this.store.select(
    MerchantSelectors.selectSiteEditError
  );
  areSitesLoaded$: Observable<boolean> = this.store.select(
    MerchantSelectors.selectSitesLoaded
  );
  isBasicDataLoaded$: Observable<boolean> = this.store.select(
    MerchantSelectors.isBasicDataLoaded
  );
  basicData$: Observable<MerchantBasicDataViewModel | undefined> =
    this.store.select(MerchantSelectors.selectBasicData);
  site$?: Observable<MerchantSiteViewModel>;

  private subs = new SubSink();

  private originalSite!: MerchantSiteViewModel;

  isFirstSite?: boolean;
  createOrEdit = '';

  constructor(
    private readonly store: Store,
    private readonly route: ActivatedRoute,
    private dialogService: DialogService,
    private readonly router: Router,
    private readonly location: Location // from @angular/common
  ) {}

  ngOnInit(): void {
    this.subs.sink = this.isBasicDataLoaded$.subscribe((loaded) => {
      if (!loaded) this.store.dispatch(MerchantActions.loadBasicData.trigger());
    });
    this.subs.sink = this.areSitesLoaded$.subscribe((loaded) => {
      if (!loaded) this.store.dispatch(MerchantActions.loadSites.trigger());
    });

    this.site$ = this.route.queryParams.pipe(
      map((params) => params['id']),
      switchMap((id?: string) =>
        this.store.select(MerchantSelectors.selectSite(id))
      ),
      filter((site): site is MerchantSiteViewModel => !!site)
    );

    this.createOrEdit = this.route.snapshot.url[0].path;

    this.subs.sink = this.site$.subscribe((site) => {
      this.originalSite = structuredClone(site);
    });
  }

  merchantTypesChanged(
    merchantTypes: Array<MerchantSiteMerchantTypeViewModel>
  ): boolean {
    const originalTypes = this.originalSite?.merchantTypes
      .map((t) => ({
        merchantType: t.merchantType,
        licenseNumber: t.licenseNumber,
      }))
      .sort((a, b) => (a.merchantType > b.merchantType ? 1 : -1));
    const newTypes = merchantTypes
      .map((t) => ({
        merchantType: t.merchantType,
        licenseNumber: t.licenseNumber,
      }))
      .sort((a, b) => (a.merchantType > b.merchantType ? 1 : -1));
    return JSON.stringify(newTypes) !== JSON.stringify(originalTypes);
  }

  onSubmit(response: { data: MerchantSiteViewModel; isFirstSite: boolean }) {
    this.isFirstSite = response.isFirstSite;
    if (response.isFirstSite) {
      this.store.dispatch(
        MerchantActions.createSite.trigger({ site: response.data })
      );
    } else {
      if (this.merchantTypesChanged(response.data.merchantTypes)) {
        this.subs.sink = this.dialogService
          .open({
            type: 'CONFIRM',
            title: 'SHARED.SAVE_SUCCESS',
            content: 'MERCHANT.SITES.EDIT.ASSIGN_SORTIMENT',
            confirmNoText: 'DIALOG.NO',
          })
          .subscribe((updateSortiment) => {
            (response.data as PostMerchantSiteRequest).sortimentUpdateRequired =
              updateSortiment;
            this.store.dispatch(
              response.data.id
                ? MerchantActions.editSite.trigger({ site: response.data })
                : MerchantActions.createSite.trigger({ site: response.data })
            );
          });
      } else {
        this.store.dispatch(
          response.data.id
            ? MerchantActions.editSite.trigger({ site: response.data })
            : MerchantActions.createSite.trigger({ site: response.data })
        );
      }
    }
  }

  onCancel() {
    this.location.back();
  }

  canDeactivate() {
    return this.isFirstSite === true ? true : this.form.canDeactivate();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}
